import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, Store, Wrench, HeartHandshake } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter } from '../components/MarketingLayout'

export default function RolunkPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>Rólunk — cukrászda csinálta, cukrászoknak | CukrászDoc</title>
        <meta name="description" content="A CukrászDoc-ot nem szoftvercég fejleszti, hanem egy működő budapesti cukrászda és rendezvényszolgáltató. Minden funkció valós, napi papírmunkából született: gyártmánylap, HACCP, allergénjelölés, nyomonkövetés." />
        <link rel="canonical" href="https://cukraszdoc.hu/rolunk" />
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <Store size={12} />
            Nem szoftvercég — egy működő cukrászda
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            Egy cukrászda csinálta — <span style={{ color: TEAL }}>mert a piacon nem talált használhatót</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>
            A CukrászDoc mögött nincs szoftvercég és befektetői kör. Egy budapesti cukrászda és rendezvényszolgáltató áll mögötte, amelyik ugyanazzal a papírmunkával küzdött minden nap, amit ez a program levesz a válladról.
          </p>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Hogyan kezdődött</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            2026-ban a Sirha kiállításon végigjártuk a standokat, és több mint harminc HACCP- és élelmiszeripari szoftvert néztünk meg. Drágák voltak, bonyolultak, és láthatóan olyanok tervezték őket, akik sosem álltak reggel hatkor a hűtő előtt hőmérőt olvasni, vagy nem töltöttek ki gyártmánylapot ötven termékhez egy vásár előtt.
          </p>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            A saját cukrászdánkban addigra Excel-táblák és kockás füzetek tucatjaiban vezettük ugyanezt. Arra jutottunk, hogy amit keresünk, azt nekünk kell megcsinálnunk — egyszerűen, a napi munkából kiindulva, olyan áron, amit egy kis cukrászda is kifizet.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Így született a CukrászDoc. Nem egy általános „compliance eszköz", hanem konkrétan a cukrászda-, pékség- és cateringmunka papírmunkájára szabva.
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Kik vagyunk</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            A CukrászDoc-ot a Divine D. Kft. fejleszti és üzemelteti. A csapat mögött a <strong>Trillázs</strong> áll: budapesti cukrászda, catering- és rendezvényszolgáltató saját cukrász műhellyel. Süti- és tortarendelés, rendezvény-gasztronómia, csapatépítő workshopok — és a hozzájuk tartozó teljes hatósági dokumentáció, amit évek óta magunknak is vezetünk.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Ez az, amiért máshogy néz ki ez a program, mint a versenytársaké: minden képernyőjét olyan ember tervezte, aki maga is használja élesben, hatósági ellenőrzés előtt.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>Amiben hiszünk</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {[
              { icon: Wrench, title: 'A napi munkából építjük', desc: 'Minden funkció egy valós, ismétlődő papírmunkából jött: gyártmánylap, HACCP-napló, allergénjelölés, alapanyag-nyomonkövetés.' },
              { icon: HeartHandshake, title: 'Megfizethető marad', desc: 'Az alap csomag kevesebb, mint napi egy kávé ára havonta. Nincs kötelező hardver, nincs beüzemelési díj.' },
              { icon: Check, title: 'A felhasználók viszik előre', desc: 'A fejlesztési sorrendet a cukrászok visszajelzései adják. Ha valami hiányzik vagy körülményes, szólj — nagy eséllyel a következő verzióban benne lesz.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="rounded-2xl p-6" style={{ background: BG, border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Nézd meg, mennyi időt vesz le rólad</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>3 receptig ingyenes, kártyaszám nélkül. Ha kérdésed van, írj — cukrász válaszol, nem ügyfélszolgálati sablon.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            Ingyenes regisztráció <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
